import API from '@beardeddevops/react.api';
import { toast } from 'react-toastify';
import Types from '../constants/Types';
import { guardOpenWorkOrderBeforeAdd } from './openWorkOrderWarning';
import { formatApiErrorMessage } from './apiErrorMessage';
import { getCheckoutOptionEntityId } from './checkoutOptionIds';
import { catalogTaxable, fflCatalogTaxable } from './catalogTaxable';

function incrementSessionCount(rootState) {
	if (!rootState || typeof rootState.set !== 'function') {
		return;
	}
	rootState.set({
		session_items_count:
			rootState.session_items_count === null
				? 1
				: 1 + rootState.session_items_count,
	});
}

export async function addInventoryToCheckout(option, { rootState } = {}) {
	const inventoryPkey = getCheckoutOptionEntityId(option, 'inventory');
	if (!inventoryPkey) {
		throw new Error('Could not determine which item to add.');
	}

	const itemLabel = option.label || 'This item';
	const proceed = await guardOpenWorkOrderBeforeAdd(inventoryPkey, itemLabel);
	if (!proceed) {
		return false;
	}

	const item = new API.BuildClass(Types.Checkouts_Items._name, API.RequestType.CREATE);
	item.object = {
		inventory: inventoryPkey,
		serial: option.serial != null ? option.serial : null,
		quantity: '1',
		taxable: catalogTaxable(option.taxable, true),
	};
	await item.submit(null, API.RequestType.CREATE);
	incrementSessionCount(rootState);
	return true;
}

export async function addServiceToCheckout(option, { rootState, existingServiceIds = [] } = {}) {
	const servicePkey = getCheckoutOptionEntityId(option, 'service');
	if (!servicePkey) {
		throw new Error('Could not determine which service to add.');
	}

	if (existingServiceIds.some((id) => String(id) === String(servicePkey))) {
		toast.info('This service is already on the checkout. Edit the line to change quantity.', {
			position: toast.POSITION.TOP_RIGHT,
		});
		return false;
	}

	const item = new API.BuildClass(Types.Checkouts_Services._name, API.RequestType.CREATE);
	item.object = {
		service: servicePkey,
		quantity: '1',
		taxable: catalogTaxable(option.taxable, true),
	};
	await item.submit(null, API.RequestType.CREATE);
	incrementSessionCount(rootState);
	return true;
}

export async function addConsignmentToCheckout(option, { rootState } = {}) {
	const consignmentPkey = getCheckoutOptionEntityId(option, 'consignment');
	if (!consignmentPkey) {
		throw new Error('Could not determine which consignment to add.');
	}

	const item = new API.BuildClass(Types.Checkouts_Consignments._name, API.RequestType.CREATE);
	item.object = {
		consignment: consignmentPkey,
		quantity: '1',
		taxable: catalogTaxable(option.taxable, true),
	};
	await item.submit(null, API.RequestType.CREATE);
	incrementSessionCount(rootState);
	return true;
}

export async function addProductToCheckout(option, { rootState } = {}) {
	const productPkey = getCheckoutOptionEntityId(option, 'product');
	if (!productPkey) {
		throw new Error('Could not determine which product to add.');
	}

	const item = new API.BuildClass(Types.Checkouts_Products._name, API.RequestType.CREATE);
	item.object = {
		product: productPkey,
		quantity: '1',
		name: option.name || undefined,
		serial: option.serial || undefined,
		sku: option.sku || undefined,
		cost: option.cost != null && option.cost !== '' ? String(option.cost) : undefined,
		taxable: fflCatalogTaxable(option.taxable, !!rootState?.ffl_force_taxable, true),
	};
	await item.submit(null, API.RequestType.CREATE);
	incrementSessionCount(rootState);
	return true;
}

export async function addToCheckout(option, context = {}) {
	switch (option.type) {
		case 'inventory':
			return addInventoryToCheckout(option, context);
		case 'service':
			return addServiceToCheckout(option, context);
		case 'consignment':
			return addConsignmentToCheckout(option, context);
		case 'product':
			return addProductToCheckout(option, context);
		default:
			toast.error('Unsupported checkout item type.', { position: toast.POSITION.TOP_RIGHT });
			return false;
	}
}

export function getCheckoutAddErrorMessage(error) {
	return formatApiErrorMessage(error, 'Could not add item to checkout.');
}
